import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link, useSearchParams } from "react-router-dom";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import PageGuide from "components/admin/PageGuide";
import PageCard from "components/admin/PageCard";
import ActionDialog from "components/admin/ActionDialog";
import SelectField from "components/admin/SelectField";
import GiftEditor, { GiftPreview } from "components/admin/GiftEditor";
import { useFetch, useRunner } from "components/admin/hooks";
import { useConfirm } from "components/admin/Feedback";
import api from "api/client";
import { useAuth } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatNumber } from "utils/format";

/** CheckInManager.RARITY_*: điểm danh chỉ có Thường và Hiếm. */
const RARITIES = [
  { value: 0, label: "Thường", color: "default" },
  { value: 1, label: "Hiếm", color: "secondary" },
];

/** Loại quà màn điểm danh vẽ được: vàng, ngọc, vật phẩm, danh hiệu (PetRealmManager.GIFT_*). */
const GIFT_TYPES = [0, 1, 2, 13];

const rarityOf = (r) => RARITIES.find((x) => x.value === r) || RARITIES[0];
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const badGift = (gift) => !gift || gift.length < 2 || !GIFT_TYPES.includes(gift[0]);

function settingsForm(data) {
  return {
    makeup_gold: data.config.makeup_gold,
    makeup_per_month: data.config.makeup_per_month,
    pool: data.pool.map((p) => ({ id: p.id, gift: p.gift, rarity: p.rarity, weight: p.weight })),
  };
}

function monthForm(month) {
  if (!month || !month.exists) return null;
  return {
    days: month.days.map((d) => ({ gift: d.gift, rarity: d.rarity })),
    milestones: month.milestones.map((m) => ({ need: m.need, gift: m.gift })),
  };
}

function RarityChip({ rarity }) {
  const r = rarityOf(rarity);
  return <Chip size="small" label={r.label} color={r.color} sx={{ height: 18 }} />;
}

RarityChip.propTypes = { rarity: PropTypes.number.isRequired };

/** Sửa một quà (dòng kho hoặc một ngày): một món quà và độ hiếm. */
function GiftDialog({ title, description, gift: initGift, rarity: initRarity, onClose, onApply }) {
  const [gift, setGift] = useState(initGift);
  const [rarity, setRarity] = useState(initRarity);
  const bad = gift.length > 0 && !GIFT_TYPES.includes(gift[0]);
  return (
    <ActionDialog
      open
      title={title}
      description={description}
      color="info"
      submitText="Áp dụng"
      disabled={badGift(gift)}
      onClose={onClose}
      onSubmit={() => onApply({ gift, rarity })}
      maxWidth="md"
    >
      <MDBox mb={2} maxWidth={240}>
        <SelectField
          fullWidth
          label="Độ hiếm"
          value={rarity}
          onChange={(e) => setRarity(Number(e.target.value))}
        >
          {RARITIES.map((r) => (
            <MenuItem key={r.value} value={r.value}>
              {r.label}
            </MenuItem>
          ))}
        </SelectField>
      </MDBox>
      {bad && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Màn điểm danh không vẽ được loại quà này, chọn vàng, ngọc, vật phẩm hoặc danh hiệu.
        </Alert>
      )}
      <GiftEditor
        value={gift.length > 0 ? [gift] : []}
        onChange={(lines) => setGift(lines.length > 0 ? lines[lines.length - 1] : [])}
        label="Quà (chỉ giữ món thêm sau cùng)"
        randomNote="Điểm danh cần quà cố định để hiện đúng icon trên ô."
      />
    </ActionDialog>
  );
}

GiftDialog.defaultProps = { description: null };
GiftDialog.propTypes = {
  title: PropTypes.node.isRequired,
  description: PropTypes.node,
  gift: PropTypes.arrayOf(PropTypes.number).isRequired,
  rarity: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
};

/** Sửa quà một mốc: nhiều món. */
function MilestoneDialog({ milestone, onClose, onApply }) {
  const [gift, setGift] = useState(milestone.gift);
  const bad = gift.some((g) => badGift(g));
  return (
    <ActionDialog
      open
      title={`Quà mốc ${milestone.need} ngày`}
      description="Tự cộng khi người chơi đạt đủ số ngày đã nhận trong tháng (tính cả ngày bù). Tối đa 8 món, nên giữ ở mức Hiếm."
      color="info"
      submitText="Áp dụng"
      disabled={gift.length === 0 || gift.length > 8 || bad}
      onClose={onClose}
      onSubmit={() => onApply({ need: milestone.need, gift })}
      maxWidth="md"
    >
      {bad && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Chỉ nhận vàng, ngọc, vật phẩm hoặc danh hiệu.
        </Alert>
      )}
      <GiftEditor value={gift} onChange={setGift} label="Quà mốc" />
    </ActionDialog>
  );
}

MilestoneDialog.propTypes = {
  milestone: PropTypes.shape({ need: PropTypes.number, gift: PropTypes.array }).isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
};

// ------------------------------------------------------------------ cấu hình + kho quà

function PoolRow({ row, saved, share, canEdit, onEdit, onWeight, onRemove }) {
  const unchanged = saved && same(saved.gift, row.gift);
  const changed =
    !saved || !unchanged || saved.rarity !== row.rarity || saved.weight !== row.weight;
  return (
    <MDBox
      display="flex"
      alignItems="center"
      gap={1.5}
      py={1}
      px={1}
      sx={{ borderBottom: 1, borderColor: "grey.200" }}
    >
      <MDBox flexGrow={1} minWidth={0}>
        <GiftPreview gift={[row.gift]} lines={unchanged ? saved.giftLines : null} />
      </MDBox>
      {changed && (
        <Chip size="small" color="info" label={saved ? "Đã sửa" : "Mới"} sx={{ height: 18 }} />
      )}
      <MDBox width={110}>
        <MDInput
          size="small"
          type="number"
          label="Trọng số"
          value={row.weight}
          disabled={!canEdit}
          inputProps={{ min: 0 }}
          onChange={(e) => onWeight(e.target.value)}
        />
      </MDBox>
      <MDTypography
        variant="caption"
        color={Number(row.weight) > 0 ? "text" : "error"}
        sx={{ width: 64, textAlign: "right" }}
      >
        {Number(row.weight) > 0
          ? `${share.toLocaleString("vi-VN", { maximumFractionDigits: 1 })}%`
          : "Tắt"}
      </MDTypography>
      {canEdit && (
        <>
          <Tooltip title="Đổi quà / độ hiếm">
            <IconButton size="small" onClick={onEdit}>
              <Icon fontSize="small">edit</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Xoá khỏi kho">
            <IconButton size="small" color="error" onClick={onRemove}>
              <Icon fontSize="small">delete</Icon>
            </IconButton>
          </Tooltip>
        </>
      )}
    </MDBox>
  );
}

PoolRow.defaultProps = { saved: null };
PoolRow.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.number,
    gift: PropTypes.arrayOf(PropTypes.number),
    rarity: PropTypes.number,
    weight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  saved: PropTypes.shape({
    gift: PropTypes.arrayOf(PropTypes.number),
    giftLines: PropTypes.array,
    rarity: PropTypes.number,
    weight: PropTypes.number,
  }),
  share: PropTypes.number.isRequired,
  canEdit: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onWeight: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

// ------------------------------------------------------------------ bảng quà tháng

function DayCell({ day, row, saved, isToday, canEdit, onEdit }) {
  const unchanged = saved && same(saved.gift, row.gift);
  const changed = !saved || !unchanged || saved.rarity !== row.rarity;
  const rare = row.rarity === 1;
  return (
    <MDBox
      onClick={canEdit ? onEdit : undefined}
      sx={{
        p: 1,
        minHeight: 112,
        borderRadius: 2,
        border: 2,
        borderColor: isToday ? "warning.main" : rare ? "secondary.main" : "grey.300",
        bgcolor: rare ? "rgba(142, 68, 196, 0.06)" : "white",
        cursor: canEdit ? "pointer" : "default",
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        "&:hover": canEdit ? { boxShadow: 3 } : undefined,
      }}
    >
      <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={0.5}>
        <MDTypography variant="caption" fontWeight="bold" color={isToday ? "warning" : "text"}>
          Ngày {day}
          {isToday ? " (hôm nay)" : ""}
        </MDTypography>
        {changed ? (
          <Chip size="small" color="info" label="Đã sửa" sx={{ height: 18 }} />
        ) : (
          <RarityChip rarity={row.rarity} />
        )}
      </MDBox>
      <MDBox flexGrow={1}>
        <GiftPreview gift={[row.gift]} lines={unchanged ? saved.giftLines : null} />
      </MDBox>
      {saved && (
        <MDTypography variant="caption" color="text">
          {formatNumber(saved.claims)} người đã nhận
        </MDTypography>
      )}
    </MDBox>
  );
}

DayCell.defaultProps = { saved: null };
DayCell.propTypes = {
  day: PropTypes.number.isRequired,
  row: PropTypes.shape({ gift: PropTypes.arrayOf(PropTypes.number), rarity: PropTypes.number })
    .isRequired,
  saved: PropTypes.shape({
    gift: PropTypes.arrayOf(PropTypes.number),
    giftLines: PropTypes.array,
    rarity: PropTypes.number,
    claims: PropTypes.number,
  }),
  isToday: PropTypes.bool.isRequired,
  canEdit: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
};

function MonthBoard({ month, isCurrent, today, canEdit, onSaved }) {
  const [run, busy] = useRunner();
  const confirm = useConfirm();
  const [form, setForm] = useState(() => monthForm(month));
  const [editDay, setEditDay] = useState(null);
  const [editMark, setEditMark] = useState(null);
  const [confirmReroll, setConfirmReroll] = useState(false);

  useEffect(() => setForm(monthForm(month)), [month]);

  const label = `tháng ${month.monthOfYear}/${month.year}`;
  const dirty = form && !same(monthForm(month), form);

  const save = async () => {
    const changedDays = form.days
      .map((d, i) => ({ d, i, old: month.days[i] }))
      .filter(({ d, old }) => !old || !same(old.gift, d.gift) || old.rarity !== d.rarity);
    const claimedChanged = changedDays.filter(({ old }) => old && old.claims > 0);
    const changedMarks = form.milestones.filter((m, i) => !same(month.milestones[i]?.gift, m.gift));
    const lines = [
      changedDays.length
        ? `Đổi quà ${changedDays.length} ngày: ${changedDays.map(({ i }) => i + 1).join(", ")}.`
        : null,
      changedMarks.length
        ? `Đổi quà mốc: ${changedMarks.map((m) => `${m.need} ngày`).join(", ")}.`
        : null,
      "Người chơi thấy quà mới khi mở lại màn điểm danh.",
      claimedChanged.length
        ? `Chú ý: ngày ${claimedChanged
            .map(({ i }) => i + 1)
            .join(", ")} đã có người nhận — họ sẽ thấy quà mới, khác quà thật họ đã được.`
        : null,
    ].filter(Boolean);
    const ok = await confirm({
      title: `Lưu bảng quà ${label}`,
      message: lines.join("\n"),
      color: claimedChanged.length ? "warning" : "info",
      confirmText: "Lưu bảng quà",
    });
    if (!ok) return;
    const { ok: saved, result } = await run(
      () => api.put(`/api/admin/checkin/month/${month.month}`, form),
      `Đã lưu bảng quà ${label}, người chơi thấy khi mở lại màn`
    );
    if (saved) onSaved(result);
  };

  const reroll = async () => {
    const { ok, result } = await run(
      () => api.post(`/api/admin/checkin/month/${month.month}/reroll`),
      `Đã random lại bảng quà ${label}`
    );
    setConfirmReroll(false);
    if (ok) onSaved(result);
  };

  const actions = canEdit && (
    <MDBox display="flex" gap={1}>
      {dirty && (
        <MDButton
          size="small"
          color="white"
          disabled={busy}
          onClick={() => setForm(monthForm(month))}
        >
          <Icon>undo</Icon>&nbsp;Bỏ sửa
        </MDButton>
      )}
      {form && (
        <MDButton size="small" color="white" disabled={!dirty || busy} onClick={save}>
          <Icon>save</Icon>&nbsp;Lưu
        </MDButton>
      )}
      <MDButton size="small" color="white" disabled={busy} onClick={() => setConfirmReroll(true)}>
        <Icon>casino</Icon>&nbsp;{month.exists ? "Random lại" : "Sinh bảng quà"}
      </MDButton>
    </MDBox>
  );

  return (
    <PageCard
      title={`Bảng quà ${label}`}
      subtitle={
        month.exists
          ? `${month.dayCount} ngày · ${formatNumber(month.claims)} lượt đã nhận${
              canEdit ? " · bấm vào ô để đổi quà rồi bấm Lưu" : ""
            }`
          : "Chưa sinh"
      }
      color={isCurrent ? "success" : "dark"}
      actions={actions}
    >
      {!month.exists && (
        <Alert severity="info">
          {isCurrent
            ? "Tháng này chưa có bảng quà: người chơi đầu tiên mở màn điểm danh sẽ tự random từ kho quà."
            : "Tháng sau chưa có bảng quà. Bấm Sinh bảng quà để random trước và xem / sửa từng ngày; không sinh thì sang tháng tự random lúc có người mở màn."}
        </Alert>
      )}
      {form && (
        <>
          <MDBox sx={{ overflowX: "auto" }}>
            <MDBox
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, minmax(120px, 1fr))",
                gap: 1,
                minWidth: 7 * 128,
              }}
            >
              {form.days.map((row, i) => (
                <DayCell
                  key={i}
                  day={i + 1}
                  row={row}
                  saved={month.days[i]}
                  isToday={isCurrent && today === i + 1}
                  canEdit={canEdit}
                  onEdit={() => setEditDay(i)}
                />
              ))}
            </MDBox>
          </MDBox>

          <MDTypography variant="h6" mt={3} mb={1}>
            Quà mốc
          </MDTypography>
          <Grid container spacing={1}>
            {form.milestones.map((m, i) => {
              const saved = month.milestones[i];
              const unchanged = saved && same(saved.gift, m.gift);
              return (
                <Grid item xs={12} md={6} lg={3} key={m.need}>
                  <MDBox
                    onClick={canEdit ? () => setEditMark(i) : undefined}
                    sx={{
                      p: 1.5,
                      height: "100%",
                      borderRadius: 2,
                      border: 2,
                      borderColor: "secondary.main",
                      cursor: canEdit ? "pointer" : "default",
                      "&:hover": canEdit ? { boxShadow: 3 } : undefined,
                    }}
                  >
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <MDTypography variant="button" fontWeight="bold">
                        {m.need} ngày
                      </MDTypography>
                      {unchanged ? (
                        <MDTypography variant="caption" color="text">
                          {formatNumber(saved.taken)} người đã nhận
                        </MDTypography>
                      ) : (
                        <Chip size="small" color="info" label="Đã sửa" sx={{ height: 18 }} />
                      )}
                    </MDBox>
                    <GiftPreview gift={m.gift} lines={unchanged ? saved.giftLines : null} />
                  </MDBox>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {editDay !== null && form && (
        <GiftDialog
          title={`Ngày ${editDay + 1} ${label}`}
          description="Bấm Áp dụng rồi bấm Lưu của bảng để lưu lên máy chủ."
          gift={form.days[editDay].gift}
          rarity={form.days[editDay].rarity}
          onClose={() => setEditDay(null)}
          onApply={(row) => {
            setForm({ ...form, days: form.days.map((d, i) => (i === editDay ? row : d)) });
            setEditDay(null);
          }}
        />
      )}
      {editMark !== null && form && (
        <MilestoneDialog
          milestone={form.milestones[editMark]}
          onClose={() => setEditMark(null)}
          onApply={(m) => {
            setForm({
              ...form,
              milestones: form.milestones.map((x, i) => (i === editMark ? m : x)),
            });
            setEditMark(null);
          }}
        />
      )}
      {confirmReroll && (
        <ActionDialog
          open
          title={month.exists ? `Random lại bảng quà ${label}?` : `Sinh bảng quà ${label}?`}
          color={month.claims > 0 ? "error" : "warning"}
          submitText={month.exists ? "Random lại" : "Sinh"}
          busy={busy}
          onClose={() => setConfirmReroll(false)}
          onSubmit={reroll}
        >
          <MDTypography variant="button" color="text">
            Random từ kho quà hiện tại: ngày chia hết cho 7 và ngày cuối tháng là Hiếm, còn lại
            Thường; mốc 7 / 14 / 21 / 28 ngày có 2 / 3 / 4 / 5 quà Hiếm.
            {month.exists && " Mọi chỗ đã sửa tay trên bảng này sẽ mất."}
          </MDTypography>
          {month.claims > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Đã có {formatNumber(month.claims)} lượt nhận trong tháng này. Người chơi sẽ thấy quà
              mới ở những ngày họ đã nhận, khác với quà thật họ đã được.
            </Alert>
          )}
        </ActionDialog>
      )}
    </PageCard>
  );
}

MonthBoard.propTypes = {
  month: PropTypes.shape({
    month: PropTypes.number,
    year: PropTypes.number,
    monthOfYear: PropTypes.number,
    dayCount: PropTypes.number,
    exists: PropTypes.bool,
    claims: PropTypes.number,
    days: PropTypes.array,
    milestones: PropTypes.array,
  }).isRequired,
  isCurrent: PropTypes.bool.isRequired,
  today: PropTypes.number.isRequired,
  canEdit: PropTypes.bool.isRequired,
  onSaved: PropTypes.func.isRequired,
};

// ------------------------------------------------------------------ tra người chơi

function PlayerLookup({ dayCount }) {
  const canSearch = useAuth().can(PERMISSIONS.PLAYER_VIEW);
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState(null);
  const [who, setWho] = useState(null);
  const [result, setResult] = useState(null);
  const [run, busy] = useRunner();

  const load = async (person) => {
    setWho(person);
    setMatches(null);
    const { ok, result: r } = await run(() =>
      api.get(`/api/admin/checkin/player/${person.userId}`)
    );
    if (ok) setResult(r);
  };

  // Có quyền xem người chơi thì tìm theo tên; không thì chỉ nhập được mã tài khoản (số).
  const search = async () => {
    const q = query.trim();
    if (!q) return;
    setResult(null);
    if (!canSearch) {
      if (/^\d+$/.test(q)) load({ userId: Number(q), name: null });
      return;
    }
    const { ok, result: r } = await run(() => api.get("/api/admin/players", { q, size: 10 }));
    if (!ok) return;
    const rows = r?.items || r || [];
    const lower = q.toLowerCase();
    const exact = rows.filter(
      (p) =>
        (p.name || "").toLowerCase() === lower ||
        (p.username || "").toLowerCase() === lower ||
        String(p.userId) === q
    );
    if (exact.length === 1) load(exact[0]);
    else if (rows.length === 1) load(rows[0]);
    else setMatches(rows);
  };

  const byDay = result ? Object.fromEntries(result.claims.map((c) => [c.day, c])) : {};
  return (
    <PageCard
      title="Tra người chơi"
      subtitle="Khách hỏi vì sao chưa nhận quà điểm danh: xem họ đã nhận những ngày nào trong tháng này"
      color="info"
    >
      <MDBox
        component="form"
        display="flex"
        gap={1}
        alignItems="center"
        flexWrap="wrap"
        mb={2}
        onSubmit={(e) => {
          e.preventDefault();
          search();
        }}
      >
        <MDInput
          size="small"
          label={canSearch ? "Tên nhân vật hoặc tên tài khoản" : "Mã tài khoản (số)"}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ minWidth: 260 }}
        />
        <MDButton type="submit" size="small" color="info" disabled={busy || !query.trim()}>
          <Icon>search</Icon>&nbsp;Xem
        </MDButton>
      </MDBox>
      {matches && matches.length === 0 && (
        <Alert severity="info">Không tìm thấy ai khớp &quot;{query.trim()}&quot;.</Alert>
      )}
      {matches && matches.length > 1 && (
        <MDBox mb={2}>
          <MDTypography variant="caption" color="text" display="block" mb={0.5}>
            Có {matches.length} người khớp, chọn đúng người:
          </MDTypography>
          <MDBox display="flex" gap={0.5} flexWrap="wrap">
            {matches.map((p) => (
              <Chip
                key={p.userId}
                label={`${p.name || "(chưa tạo nhân vật)"} · ${p.username || p.userId}`}
                onClick={() => load(p)}
              />
            ))}
          </MDBox>
        </MDBox>
      )}
      {result && (
        <>
          {who?.name && (
            <MDTypography variant="button" display="block" mb={1}>
              <Link to={`/players/${who.userId}`}>{who.name}</Link>
              {who.username ? ` · tài khoản ${who.username}` : ""}
            </MDTypography>
          )}
          <MDBox display="flex" gap={1} flexWrap="wrap" mb={2}>
            <Chip label={`Đã nhận ${result.claims.length} ngày`} color="success" />
            <Chip
              label={`Bù ${
                result.claims.filter((c) => c.makeupGold > 0).length
              } ngày · ${formatNumber(result.claims.reduce((s, c) => s + c.makeupGold, 0))} vàng`}
            />
            <Chip
              label={`Quà mốc đã nhận: ${
                result.milestones.length > 0
                  ? result.milestones.map((m) => `${m.need} ngày`).join(", ")
                  : "chưa có"
              }`}
            />
            <Chip label={`Màn điểm danh tự bật lần cuối: ${result.popupDay || "chưa"}`} />
          </MDBox>
          <MDBox display="flex" gap={0.5} flexWrap="wrap">
            {Array.from({ length: dayCount }, (_, i) => {
              const c = byDay[i + 1];
              const title = c
                ? `${
                    c.makeupGold > 0 ? `Bù ${formatNumber(c.makeupGold)} vàng` : "Đúng ngày"
                  } · ${new Date(c.createdAt).toLocaleString("vi-VN")}`
                : "Chưa nhận";
              return (
                <Tooltip key={i} title={title}>
                  <Chip
                    size="small"
                    label={i + 1}
                    color={!c ? "default" : c.makeupGold > 0 ? "warning" : "success"}
                    variant={c ? "filled" : "outlined"}
                    sx={{ minWidth: 36 }}
                  />
                </Tooltip>
              );
            })}
          </MDBox>
          <MDTypography variant="caption" color="text" display="block" mt={1}>
            Xanh: nhận đúng ngày · Cam: nhận bù bằng vàng · Viền trắng: chưa nhận. Rê chuột lên ô để
            xem giờ nhận.
          </MDTypography>
        </>
      )}
    </PageCard>
  );
}

PlayerLookup.propTypes = { dayCount: PropTypes.number.isRequired };

// ------------------------------------------------------------------ trang

// Tab ghi lên URL (?tab=); mặc định "Tháng này" — việc hay xem nhất.
const TABS = [
  { key: "current", label: "Tháng này" },
  { key: "next", label: "Tháng sau" },
  { key: "pool", label: "Kho quà & cài đặt" },
  { key: "lookup", label: "Tra người chơi" },
];
const TAB_SX = { flex: { xs: "0 0 auto", md: "1 1 auto" }, px: 1.5, whiteSpace: "nowrap" };

function CheckIn() {
  const canEdit = useAuth().can(PERMISSIONS.CHECKIN_EDIT);
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "current";
  const pickTab = (v) => setParams(v === "current" ? {} : { tab: v }, { replace: true });
  const page = useFetch(() => api.get("/api/admin/checkin"), []);
  const [run, busy] = useRunner();
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (page.data) setForm(settingsForm(page.data));
  }, [page.data]);

  const data = page.data;
  const dirty = data && form && !same(settingsForm(data), form);
  const set = (patch) => setForm({ ...form, ...patch });
  const setRow = (index, patch) =>
    set({ pool: form.pool.map((r, i) => (i === index ? { ...r, ...patch } : r)) });
  const totalOf = (rarity) =>
    form
      ? form.pool
          .filter((r) => r.rarity === rarity)
          .reduce((s, r) => s + (Number(r.weight) || 0), 0)
      : 0;

  // Rời trang khi kho quà / cài đặt còn thay đổi chưa lưu thì trình duyệt hỏi lại.
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const saveSettings = async () => {
    const { ok, result } = await run(
      () =>
        api.put("/api/admin/checkin/settings", {
          makeup_gold: Number(form.makeup_gold) || 0,
          makeup_per_month: Number(form.makeup_per_month) || 0,
          pool: form.pool.map((r) => ({ ...r, weight: Number(r.weight) || 0 })),
        }),
      "Đã lưu cấu hình điểm danh. Kho quà mới dùng cho lần random kế tiếp"
    );
    if (ok) page.setData(result);
  };

  const actions = (
    <MDBox display="flex" gap={1}>
      {canEdit && dirty && (
        <MDButton
          size="small"
          color="white"
          disabled={busy}
          onClick={() => setForm(settingsForm(data))}
        >
          <Icon>undo</Icon>&nbsp;Bỏ sửa
        </MDButton>
      )}
      {canEdit && (
        <MDButton size="small" color="white" disabled={!dirty || busy} onClick={saveSettings}>
          <Icon>save</Icon>&nbsp;Lưu
        </MDButton>
      )}
      <Tooltip title="Tải lại (bỏ phần chưa lưu)">
        <MDButton size="small" color="white" onClick={() => page.reload()}>
          <Icon>refresh</Icon>
        </MDButton>
      </Tooltip>
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="checkin" />
        {page.error && (
          <Alert severity="error">Không tải được dữ liệu điểm danh: {page.error}</Alert>
        )}
        {data && form && (
          <>
            <MDBox display="flex" gap={1} flexWrap="wrap" mb={2}>
              <Chip label={`Hôm nay: ${formatNumber(data.stats.today)} người nhận`} />
              <Chip label={`Tháng này: ${formatNumber(data.stats.players)} người điểm danh`} />
              <Chip label={`${formatNumber(data.stats.month)} lượt nhận`} />
              {dirty && <Chip color="warning" label="Kho quà / cài đặt có thay đổi chưa lưu" />}
            </MDBox>
            <Tabs
              value={tab}
              onChange={(e, v) => pickTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 1, maxWidth: 720 }}
            >
              {TABS.map((t) => (
                <Tab key={t.key} value={t.key} label={t.label} sx={TAB_SX} />
              ))}
            </Tabs>
            {tab === "pool" && (
              <>
                <MDBox mb={3}>
                  <PageCard
                    title="Điểm danh bù & thống kê tháng này"
                    subtitle="Ngày theo giờ máy chủ, sang ngày lúc 0h"
                    color="warning"
                    actions={actions}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <MDInput
                          fullWidth
                          type="number"
                          label="Giá bù một ngày (vàng)"
                          value={form.makeup_gold}
                          disabled={!canEdit}
                          inputProps={{ min: 0 }}
                          helperText="0 = bù miễn phí"
                          onChange={(e) => set({ makeup_gold: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <MDInput
                          fullWidth
                          type="number"
                          label="Số ngày bù tối đa mỗi tháng"
                          value={form.makeup_per_month}
                          disabled={!canEdit}
                          inputProps={{ min: 0, max: 31 }}
                          helperText="0 = không giới hạn"
                          onChange={(e) => set({ makeup_per_month: e.target.value })}
                        />
                      </Grid>
                    </Grid>
                    <MDBox display="flex" gap={1} flexWrap="wrap" mt={2}>
                      <Chip label={`Hôm nay: ${formatNumber(data.stats.today)} người nhận`} />
                      <Chip label={`Tháng này: ${formatNumber(data.stats.month)} lượt nhận`} />
                      <Chip label={`${formatNumber(data.stats.players)} người điểm danh`} />
                      <Chip
                        label={`Bù ${formatNumber(data.stats.makeups)} ngày · thu ${formatNumber(
                          data.stats.makeupGold
                        )} vàng`}
                      />
                      <Chip label={`${formatNumber(data.stats.milestones)} lần nhận quà mốc`} />
                    </MDBox>
                  </PageCard>
                </MDBox>

                <MDBox mb={3}>
                  <PageCard
                    title="Kho quà random"
                    subtitle="Mỗi tháng random một lần từ kho này. % là tỉ lệ trong cùng độ hiếm"
                    color="info"
                    actions={actions}
                  >
                    <Grid container spacing={3}>
                      {RARITIES.map((r) => {
                        const total = totalOf(r.value);
                        return (
                          <Grid item xs={12} lg={6} key={r.value}>
                            <MDBox
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={1}
                            >
                              <MDBox display="flex" alignItems="center" gap={1}>
                                <MDTypography variant="h6">Quà {r.label}</MDTypography>
                                <MDTypography variant="caption" color="text">
                                  {r.value === 1
                                    ? "ngày chia hết cho 7, ngày cuối tháng và quà mốc"
                                    : "các ngày còn lại"}
                                </MDTypography>
                              </MDBox>
                              {canEdit && (
                                <MDButton
                                  size="small"
                                  variant="outlined"
                                  color="info"
                                  onClick={() => setEditing({ index: -1, rarity: r.value })}
                                >
                                  <Icon>add</Icon>&nbsp;Thêm
                                </MDButton>
                              )}
                            </MDBox>
                            {total <= 0 && (
                              <Alert severity="error" sx={{ mb: 1 }}>
                                Không có quà {r.label} nào bật, điểm danh sẽ báo bảo trì.
                              </Alert>
                            )}
                            {form.pool.map((row, i) =>
                              row.rarity !== r.value ? null : (
                                <PoolRow
                                  key={row.id || `new-${i}`}
                                  row={row}
                                  saved={
                                    data.pool.find((p) => p.id === row.id && row.id > 0) || null
                                  }
                                  share={total > 0 ? ((Number(row.weight) || 0) * 100) / total : 0}
                                  canEdit={canEdit}
                                  onEdit={() => setEditing({ index: i })}
                                  onWeight={(w) => setRow(i, { weight: w })}
                                  onRemove={() =>
                                    set({ pool: form.pool.filter((_, j) => j !== i) })
                                  }
                                />
                              )
                            )}
                          </Grid>
                        );
                      })}
                    </Grid>
                  </PageCard>
                </MDBox>
              </>
            )}

            {tab === "current" && (
              <MDBox mb={3}>
                <MonthBoard
                  month={data.current}
                  isCurrent
                  today={data.today}
                  canEdit={canEdit}
                  onSaved={page.setData}
                />
              </MDBox>
            )}
            {tab === "next" && (
              <MDBox mb={3}>
                <MonthBoard
                  month={data.next}
                  isCurrent={false}
                  today={data.today}
                  canEdit={canEdit}
                  onSaved={page.setData}
                />
              </MDBox>
            )}
            {tab === "lookup" && <PlayerLookup dayCount={data.current.dayCount} />}
          </>
        )}
      </MDBox>
      {editing !== null && form && (
        <GiftDialog
          title={editing.index < 0 ? "Thêm quà vào kho" : "Sửa quà trong kho"}
          description="Bấm Áp dụng rồi bấm Lưu ở trang để lưu lên máy chủ. Vật phẩm nên khoá giao dịch."
          gift={editing.index < 0 ? [] : form.pool[editing.index].gift}
          rarity={editing.index < 0 ? editing.rarity : form.pool[editing.index].rarity}
          onClose={() => setEditing(null)}
          onApply={({ gift, rarity }) => {
            if (editing.index < 0)
              set({ pool: [...form.pool, { id: 0, gift, rarity, weight: 10 }] });
            else setRow(editing.index, { gift, rarity });
            setEditing(null);
          }}
        />
      )}
      <Footer />
    </DashboardLayout>
  );
}

export default CheckIn;
